import {
  handleClientSideSubmitResult,
  initializeStripeElement,
  submitToStripe,
} from '../../../../common/components/stripe/index'

import { hideLoading, onPaymentMethodChange } from '../common'
import { attachDynamicEvent } from '~/common/helpers/dom'
import { processFormSubmission } from './common'
import { prepareStripeCheckout } from './stripe_setup'
import {
  GENERIC_SETUP_ERROR,
  RECOVERABLE_FAILURE,
  replacesFailure,
} from './stripe_setup_outcome'

/*
  Stripe payments can come in two forms:

  A new payment method.

  1. Initialize the Stripe UI (initializeForStripePaymentProcessor).
  2. On form submit, check if required Stripe inputs are filled
        a. If true, create a Transaction, then call the Stripe client-side code (submitWithNewStripe). This will handle
           validation and 3DS and stuff.
        b. If false, prevent form submission (avoid creating Simplero abandoned purchases), and show the stripe errors element.
  3. If Stripe is successful, it will go to the transaction's callback_url, which is a page which will wait until we
     receive word from Stripe that things worked.

  An existing payment method

  1. On form submit, attempt to charge server-side (submitWithSavedStripe). If that succeeds, go to the transaction's
     callback_url.
  2. If the server-side attempt failed, try Stripe's client-side code, which will handle 3DS (submitWithSavedStripe).
  3. If Stripe is successful, go to the transaction's callback_url.
*/

// Reveal the card slot (and its local spinner) without dimming the rest of
// the checkout. Page-level showLoading() used to add pointer-events-none
// until stripe:ready, so a cold Payment Element boot (hCaptcha, wallets)
// made the whole form unusable for tens of seconds.
function showStripeCardSlot() {
  const fields = document.getElementById('stripe_fields')
  if (fields) {
    fields.classList.remove('hidden')
    fields.style.display = 'block'
  }
  const loader = document.getElementById('stripe_card_element_loader')
  if (loader) loader.hidden = false
}

function hideStripeCardLoader() {
  const loader = document.getElementById('stripe_card_element_loader')
  if (loader) loader.hidden = true
}

// Last resort for a Stripe that neither becomes ready nor reports an error — an unknown failure, or
// one Stripe swallows. Submit stays HTML-disabled until ready; the watchdog still needs to unlock
// the form so a swallowed error is not a dead end. Generous, because firing it on a slow phone
// connection would replace a working form with an error.
const STRIPE_READY_TIMEOUT_MS = 25000

// The setup attempt currently on screen, or null before the first one. The buyer can switch payment
// processor while one is in flight, so a late response, a late Stripe event, or a watchdog from an
// abandoned attempt must not touch the one now showing. Stripe's events are matched on
// `clientSecret` — every attempt gets its own intent, and every caller mounts into the same shared
// #stripe_card_element, so the secret is what ties an event back to the mount that produced it.
let currentSetup = null

function abandonCurrentSetup() {
  if (!currentSetup) return

  clearTimeout(currentSetup.watchdog)
  // Our message leaves with us. Otherwise a failed Stripe leaves "card payments are unavailable"
  // sitting above the PayPal form the buyer just switched to, and a Stripe B that mounts fine after
  // a failed Stripe A arrives as a fresh, non-failed setup that has nothing to clear.
  hideStripeSetupError(currentSetup.message)
  currentSetup = null
}

// Express checkout temporarily removes the ordinary payment fields. Ignore
// late setup responses while they are detached, then restart only if the same
// processor option is restored (a cart refresh will initialize its replacement).
export function suspendStripeSetup() {
  const selectedProcessor = getSelectedStripeProcessorOption()
  abandonCurrentSetup()
  if (selectedProcessor) disableSubmitButtons()

  return () => {
    if (
      selectedProcessor?.isConnected &&
      getSelectedStripeProcessorOption() === selectedProcessor
    ) {
      initializeForStripePaymentProcessor(selectedProcessor.value)
    }
  }
}

// A failed setup stays current so submitWithNewStripe can refuse it, but its watchdog is stopped and
// nothing Stripe says afterwards is acted on.
//
// `ready` is cleared as well, so failed can never coexist with ready. It could: a split-payment
// re-mount inherits the previous intent's readiness (see retargetStripeSetup), so a re-mount Stripe
// then failed to load would have painted "card payments are unavailable" and accepted the submit
// half a second later — posting the form against an intent whose Payment Element never loaded.
//
// `kind` is what onStripeReady consults to decide whether a failure may be taken back. Only
// 'watchdog' may: it is the one we guessed at. An explicit failure arriving after a watchdog
// therefore has to be able to replace it — see replacesFailure — or watchdog then loaderror then
// ready would recover a mount Stripe had already said was dead.
function failSetup(setup, kind, message) {
  if (setup !== currentSetup) return
  if (setup.failed && !replacesFailure(setup.failure, kind)) return

  clearTimeout(setup.watchdog)
  setup.failed = true
  setup.failure = kind
  setup.ready = false
  setup.message = message || GENERIC_SETUP_ERROR
  showStripeSetupError(setup.message)
}

function onStripeReady(event) {
  const setup = currentSetup
  if (!setup) return
  if (event.detail?.clientSecret !== setup.clientSecret) return

  clearTimeout(setup.watchdog)

  // A mount Stripe explicitly refused to load is terminal. Nothing here relies on Stripe never
  // following a loaderror with a ready — it may well not, but that is its guarantee to give, not
  // ours to assume, and enabling submit after telling the buyer card payments are unavailable is
  // much the worse of the two mistakes.
  if (setup.failed && setup.failure !== RECOVERABLE_FAILURE) return

  // The watchdog is the one failure we guessed at, so it is the one we can take back: on a slow
  // enough connection it fires on a form that was merely still loading, and leaving it stranded is
  // its own bug — re-selecting the same processor fires no change event, so there is no way back
  // short of a reload.
  if (setup.failed) {
    hideStripeSetupError(setup.message)
    setup.failed = false
    setup.failure = null
    setup.message = null
  }

  setup.ready = true

  let bancontactNotice = document.getElementById('stripe_bancontact_notice')
  if (bancontactNotice) {
    bancontactNotice.style.display = 'none'
  }
  const fields = document.getElementById('stripe_fields')
  if (fields) fields.style.display = 'block'
  hideStripeCardLoader()
  hideLoading()
  enableSubmitButtons()
}

// Stripe could not load the payment form. The case that brought us here is a publishable key that
// cannot read this intent's client_secret — Stripe answers 400 and never fires `ready`, so before
// this the buyer sat on a dimmed page with nothing to explain it.
function onStripeLoadError(event) {
  const setup = currentSetup
  if (!setup) return
  if (event.detail?.clientSecret !== setup.clientSecret) return

  console.warn('Stripe could not load the payment form', event.detail?.error)
  failSetup(setup, 'loaderror', unavailableMessage())
}

function onStripeAvailablePaymentMethodsChange(event) {
  const setup = currentSetup
  if (!setup || setup.failed || !getSelectedStripeProcessorOption()) return
  if (event.detail?.clientSecret !== setup.clientSecret) return
  if (
    !event.target.isConnected ||
    event.target !== document.getElementById('stripe_card_element')
  )
    return

  const types = Object.entries(event.detail.paymentMethods || {})
    .filter(([, method]) => method?.available === true)
    .map(([type]) =>
      type.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
    )
    .filter((type) => !setup.excludedWallets?.includes(type))
  const icons = event.target
    .closest('form')
    ?.querySelector(
      `[data-stripe-payment-method-processor-id="${setup.paymentProcessorId}"]`
    )
  if (!icons) return

  // Keep the latest list on this tile so a lazily loaded controller sees it on connect too.
  icons.dataset.stripePaymentMethods = JSON.stringify(types)
  icons.dispatchEvent(new CustomEvent('stripe:payment-methods'))
}

// The split-payment amount change re-creates the intent and re-mounts into this same
// #stripe_card_element without going through initializeForStripePaymentProcessor. Point the setup
// record at the new secret, before that mount, so its stripe:ready / stripe:loaderror are recognized
// rather than filtered out as another mount's — otherwise a re-mount Stripe cannot load leaves an
// empty card box and no error at all.
//
// Readiness is deliberately carried over rather than reset: that flow enables submit itself, right
// after mount and before `ready` arrives, so refusing every submit in the window before the event
// would break a checkout that works today. A re-mount that actually fails is still refused —
// failSetup clears `ready` — so this inherits optimism, not a failure it cannot see.
export function retargetStripeSetup(clientSecret) {
  const setup = currentSetup
  if (!setup) return

  clearTimeout(setup.watchdog)
  // Before the state is dropped, so the message goes with it. Otherwise a mount that had failed
  // leaves "card payments are unavailable" on screen, the replacement mounts fine, and the buyer is
  // looking at a working, submittable form that says card payments are unavailable. If the
  // replacement fails too, loaderror paints it again.
  hideStripeSetupError(setup.message)
  setup.clientSecret = clientSecret
  setup.failed = false
  setup.failure = null
  setup.message = null
  // Coupon / price remounts re-render #stripe_fields as hidden.
  // Stripe will not paint an iframe into a display:none parent.
  showStripeCardSlot()
  setup.watchdog = setTimeout(
    () => failSetup(setup, RECOVERABLE_FAILURE),
    STRIPE_READY_TIMEOUT_MS
  )
}

// Attached once per element rather than once per attempt: per-attempt listeners either accumulate,
// or — with { once: true } — let a late event from an abandoned attempt consume the listener the
// live one was waiting on. A re-rendered element arrives without the flag and gets its own pair.
function ensureStripeListeners(cardElement) {
  if (!cardElement || cardElement.dataset.stripeListenersAttached) return

  cardElement.dataset.stripeListenersAttached = 'true'
  cardElement.addEventListener('stripe:ready', onStripeReady)
  cardElement.addEventListener('stripe:loaderror', onStripeLoadError)
  cardElement.addEventListener(
    'stripe:availablepaymentmethodschange',
    onStripeAvailablePaymentMethodsChange
  )
}

// Set up the UI for a new Stripe payment method
function initializeForStripePaymentProcessor(value) {
  disableSubmitButtons()
  showStripeCardSlot()

  const cardElement = document.getElementById('stripe_card_element')
  if (!cardElement) {
    // Submit was disabled a moment ago, and without a mount node the Payment
    // Element can never signal ready to re-enable it. Paint the retryable
    // error rather than leaving the buyer on a checkout that cannot be paid.
    abandonCurrentSetup()
    showStripeSetupError()
    return
  }

  abandonCurrentSetup()
  ensureStripeListeners(cardElement)

  const setup = {
    clientSecret: null,
    ready: false,
    failed: false,
    failure: null,
    message: null,
  }
  setup.watchdog = setTimeout(
    () => failSetup(setup, RECOVERABLE_FAILURE),
    STRIPE_READY_TIMEOUT_MS
  )
  currentSetup = setup

  value = value.replace(/pp-/, '')
  const [paymentProcessorId, paymentProcessorSubtype] = value.split('-')
  setup.paymentProcessorId = paymentProcessorId

  prepareStripeCheckout(
    document.querySelector('form.cart-checkout-payment'),
    paymentProcessorId,
    paymentProcessorSubtype ?? null
  )
    .then((json) => {
      if (setup !== currentSetup) return

      setup.clientSecret = json.client_secret
      setup.excludedWallets = json.excluded_wallets || []

      let submitButton = getSubmitButton()
      if (!submitButton) return

      submitButton.setAttribute('data-publishable-key', json.publishable_key)
      submitButton.setAttribute('data-client-secret', json.client_secret)
      submitButton.setAttribute('data-intent-id', json.intent_id)
      submitButton.setAttribute('data-intent-type', json.intent_type)

      if (paymentProcessorSubtype == 'bancontact') {
        let stripeFieldsElement = document.querySelector('#stripe_fields')
        if (stripeFieldsElement) {
          stripeFieldsElement.style.display = 'none'
        }
        hideStripeCardLoader()
        document.getElementById('stripe_bancontact_notice').style.display =
          'block'
        // Bancontact mounts no element, so no stripe:ready is coming.
        clearTimeout(setup.watchdog)
        setup.ready = true
        hideLoading()
        enableSubmitButtons()
      } else {
        // Coupon / price remounts re-render #stripe_fields as hidden.
        // Stripe will not paint an iframe into a display:none parent.
        showStripeCardSlot()
        // Returned, so that anything Stripe throws while mounting — an unusable key, a blocked
        // js.stripe.com — lands in the catch below rather than in an unhandled rejection that
        // leaves the checkout loading forever.
        return initializeStripeElement(
          json.publishable_key,
          json.client_secret,
          '#stripe_card_element',
          {
            automaticPaymentMethods: json.automatic_payment_methods,
            futureCharge: json.future_charge,
            // Only set for a setup intent Stripe picks the methods for: it has no currency of its own to filter
            // them by, and an intent pinned to payment_method_types has nothing left to filter.
            setupCurrency: json.currency,
            excludedTypes: json.excluded_types,
            excludedWallets: json.excluded_wallets,
          }
        )
      }
    })
    .catch((error) => {
      console.warn('Stripe payment setup failed', error)
      // Before receiving the secret, the shared helper supplies buyer-facing server errors.
      // Once mounting begins, keep Stripe.js loading failures behind the generic message.
      failSetup(
        setup,
        'setup',
        setup.clientSecret ? GENERIC_SETUP_ERROR : error.message
      )
    })
}

// Show a retryable error when /stripe_setup fails (transient Stripe outage or 5xx)
// instead of leaving the spinner stuck and the submit buttons disabled.
function showStripeSetupError(message) {
  let errorElement = getErrorElement()
  if (!errorElement) return

  errorElement.querySelector('.error-message').innerText =
    message || GENERIC_SETUP_ERROR
  errorElement.style.display = 'block'
  errorElement.classList.remove('hidden')
  hideStripeCardLoader()
  hideLoading()
  enableSubmitButtons()
}

// Takes down only the message this setup put up. #stripe_errors is shared: submitWithNewStripe
// paints Stripe confirmation failures there, and the payment form controller paints its
// "card details not completed" text, and either can be showing by the time a late ready arrives.
// Recovering from our own watchdog must not wipe someone else's error that is still true.
function hideStripeSetupError(message) {
  if (!message) return

  let errorElement = getErrorElement()
  if (!errorElement) return
  if (errorElement.querySelector('.error-message').innerText !== message) return

  errorElement.style.display = 'none'
  // `hidden` carries !important, which is what showStripeSetupError removes to reveal it.
  errorElement.classList.add('hidden')
}

// Translated server-side and carried on the element, since this file has no gettext.
function unavailableMessage() {
  const errorElement = getErrorElement()
  return errorElement?.dataset.unavailableMessage || GENERIC_SETUP_ERROR
}

function chooseNewStripe(event) {
  initializeForStripePaymentProcessor(event.target.value)
}

function chooseMaybeNonStripe(event) {
  if (event.target.closest('.payment-processor-option-stripe') == null) {
    chooseNonStripe()
  }
}

function chooseNonStripe() {
  hideStripeCardLoader()
  document
    .querySelectorAll('#stripe_fields, #stripe_bancontact_notice')
    .forEach((el) => (el.style.display = 'none'))

  // Abandons any Stripe setup still in flight, so its response cannot re-dim the page after the
  // buyer has moved on.
  abandonCurrentSetup()
  // showLoading() blanks pointer events across the whole checkout. Without this, a buyer who hit a
  // Stripe that never loaded and then picked PayPal stayed stuck behind the overlay — the submit
  // buttons were enabled but unclickable, and only a reload got them out.
  hideLoading()
  enableSubmitButtons()
}

function stripeBootInProgress() {
  return document.querySelector('.js-disabled-for-stripe') != null
}

function submitIfWithNewStripe(event) {
  if (stripeBootInProgress()) {
    event.preventDefault()
    return
  }
  if (getSelectedStripeProcessorOption()) {
    submitWithNewStripe(event)
  }
}

function submitIfWithSavedStripe(event) {
  if (getSelectedSavedStripeOption()) {
    submitWithSavedStripe(event)
  }
}

function preventIncompleteStripeSubmission(event) {
  if (event.target.dataset.preventIncompleteStripeSubmit !== 'true') return
  if (!getSelectedStripeProcessorOption()) return

  const incompleteStripeElement = event.target.querySelector(
    '.js-stripe-is-not-completed:not(.hidden)'
  )
  if (!incompleteStripeElement || incompleteStripeElement.offsetParent === null)
    return

  event.preventDefault()
  event.stopPropagation()

  const errorElement = getErrorElement()
  errorElement.querySelector('.error-message').innerText =
    getSubmitButton().dataset.notCompletedText
  errorElement.style.display = 'block'
  errorElement.classList.remove('hidden')
}

function submitButtons() {
  // Both pay buttons — the in-form one and the md:hidden mobile duplicate,
  // which carries the same classes. A bare button[type=submit] would also
  // match unrelated submits such as the promo-code Apply.
  return document.querySelectorAll('.submit-payment')
}

function disableSubmitButtons() {
  // HTML disabled (not just t-btn-disabled) so Enter / implicit submit
  // cannot pay while the Payment Element is still booting.
  submitButtons().forEach((submitButton) => {
    submitButton.classList.add('t-btn-disabled', 'js-disabled-for-stripe')
    submitButton.disabled = true
  })
}

function enableSubmitButtons() {
  document
    .querySelectorAll('.js-disabled-for-stripe')
    .forEach((submitButton) => {
      submitButton.classList.remove('t-btn-disabled', 'js-disabled-for-stripe')
      submitButton.disabled = false
    })
}

async function submitWithNewStripe(event) {
  event.preventDefault()
  if (stripeBootInProgress()) return

  // A setup we know did not finish: submitting anyway posts the form, which creates an abandoned
  // purchase, and only then fails at Stripe with nothing the buyer can act on. Re-state why instead.
  // The submit buttons are deliberately left usable so switching payment method still works.
  //
  // Deliberately fails open on a missing currentSetup rather than refusing: this handler also fires
  // for a saved card when a single Stripe processor renders as a hidden input, and chooseNonStripe()
  // clears the setup on the way there. Only an attempt that actually ran and did not become ready
  // is refused.
  if (currentSetup && !currentSetup.ready) {
    showStripeSetupError(currentSetup.message || unavailableMessage())
    return
  }

  let submitButton = getSubmitButton()
  if (!submitButton) return
  let form = event.target

  getErrorElement().style.display = 'none'

  const { intentId, intentType } = getIntentIdAndType()

  processFormSubmission(form, {
    getFormData: () => {
      let formData = new FormData(form)
      formData.append('intent_id', intentId)
      formData.append('intent_type', intentType)
      return formData
    },
    onSuccess: async (json) => {
      const error = await submitToStripe(
        submitButton.getAttribute('data-publishable-key'),
        submitButton.getAttribute('data-client-secret'),
        json.callback_url,
        intentType == 'setup',
        getSelectedSubtype(),
        submitButton.getAttribute('data-customer-name'),
        submitButton.getAttribute('data-customer-email'),
        '#stripe_card_element'
      )

      if (error) {
        let errorElement = getErrorElement()
        errorElement.querySelector('.error-message').innerText = error.message
        errorElement.style.display = 'block'
        errorElement.classList.remove('hidden') // hidden have !important, 'block' does not work in some cases
        hideLoading()
      }
    },
  })
}

function getErrorElement() {
  return document.getElementById('stripe_errors')
}

function getSubmitButton() {
  return document.querySelector(
    '.cart-checkout-payment .submit-payment, .cart-checkout-payment button[type="submit"]'
  )
}

export function getIntentIdAndType() {
  const button = getSubmitButton()
  return {
    intentId: button.dataset.intentId,
    intentType: button.dataset.intentType,
  }
}

// Attempt to charge server-side. If that fails, go client-side (for 3DS).
async function submitWithSavedStripe(event) {
  event.preventDefault()

  let submitButton = getSubmitButton()
  let form = event.target

  processFormSubmission(form, {
    onSuccess: async (json) => {
      let publishableKey = getSelectedSavedStripeOption().getAttribute(
        'data-publishable-key'
      )
      let error = await handleClientSideSubmitResult(
        json,
        publishableKey,
        null,
        submitButton.getAttribute('data-customer-name'),
        submitButton.getAttribute('data-customer-email')
      )
      if (error) {
        alert('Sorry, we could not complete your request. ' + error)
        hideLoading()
      }
    },
  })
}

function getSelectedStripeProcessorOption() {
  return document.querySelector(
    '.cart-checkout-payment .payment-processor-option-stripe input[type=radio]:checked, ' +
      ".cart-checkout-payment input[type=hidden][name='payment_method'].payment-processor-option-stripe"
  )
}

function getSelectedSavedStripeOption() {
  return document.querySelector(
    '.cart-checkout-payment .payment-method-option-stripe input[type=radio]:checked'
  )
}

function getSelectedSubtype() {
  let value = getSelectedStripeProcessorOption().value
  value = value.replace(/pp-/, '')
  const [_paymentProcessorId, paymentProcessorSubtype] = value.split('-')
  return paymentProcessorSubtype
}

onPaymentMethodChange(chooseMaybeNonStripe)
attachDynamicEvent(
  '.cart-checkout-payment .payment-processor-option-stripe input[type=radio]',
  'change',
  chooseNewStripe
)
attachDynamicEvent(
  'form.cart-checkout-payment',
  'submit',
  preventIncompleteStripeSubmission,
  { capture: true }
)
attachDynamicEvent(
  'form.cart-checkout-payment',
  'submit',
  submitIfWithNewStripe
)
attachDynamicEvent(
  'form.cart-checkout-payment',
  'submit',
  submitIfWithSavedStripe
)
